/**
 * View component for /content-modules/:contentModuleId
 *
 * Displays a single contentModule from the 'byId' map in the contentModule reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, history, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// import third-party libraries
import _ from 'lodash';
import ReactTooltip from 'react-tooltip';
import Scrollchor from 'react-scrollchor';
import classNames from 'classnames';
import renderHTML from 'react-render-html';
import { DateTime } from 'luxon';

// import actions
import * as courseActions from '../../course/courseActions';
import * as evaluationActions from '../../evaluation/evaluationActions';
import * as moduleCertificateActions from '../../moduleCertificate/moduleCertificateActions';
import * as nodeActions from '../../node/nodeActions';
import * as resourceLinkActions from '../../resourceLink/resourceLinkActions';
import * as sceneActions from '../../scene/sceneActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as contentModuleActions from '../contentModuleActions';
import * as userEvaluationActions from '../../userEvaluation/userEvaluationActions';
import * as userModuleActions from '../../userModule/userModuleActions';
import * as userNodeActions from '../../userNode/userNodeActions';
import * as userEventActions from '../../userEvent/userEventActions';
import * as sentimentAnswers from '../../sentimentAnswer/sentimentAnswerActions';

// import global components
import AlertModal from '../../../global/components/modals/AlertModal.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';
import VideoPlayer from "../../../global/components/helpers/VideoPlayer.js.jsx";

// import utilities
import certUtils from '../../../global/utils/certUtils';
import richContentUtils from '../../../global/utils/richContentUtils';
import videoUtils from "../../../global/utils/videoUtils.js";

// import module components
import AchievedModuleCertificate from '../../moduleCertificate/components/AchievedModuleCertificate.js.jsx';
import CheckpointMarker from '../../node/components/CheckpointMarker.js.jsx';
import EmptyModuleCertificate from '../../moduleCertificate/components/EmptyModuleCertificate.js.jsx';
import ResourceLinkListItem from '../../resourceLink/components/ResourceLinkListItem.js.jsx';
import ContentModuleLayout from '../components/ContentModuleLayout.js.jsx';
import ContentModuleTableOfContents from '../components/ContentModuleTableOfContents.js.jsx';
import UserSentimentModal from '../components/UserSentimentModal.js.jsx';

// import contentModule css modules
import contentModuleStyles from '../contentModuleModuleStyles.css';
class ContentModuleConclusion extends Base {
  constructor(props) {
    super(props);
    this.state = {
      startModuleAlert: false
      , tocVisible: true
      , sentimentModalVisible: false
      , redirectStrategies: []
    }
    this._bind(
      '_goToStartPage'
      , '_onNodeClick'
      , '_onVideoComplete'
      , '_startEvaluation'
      , '_toggleTOC'
      , '_toggleSentimentModal'
      , '_closeSentimentModal'
    )
  }

  componentDidMount() {
    const { dispatch, loggedInUser, match, studyStore } = this.props;

    // pre-emptively invalidate the agenda items, so that when they navigate back they will see everything
    // but, we dont know which studies it might apply to
    // so invalidate all of them!
    const { byId } = studyStore;
    // console.log("DEBUG")
    for (let nextStudyId in byId) {
      // console.log("next study id", nextStudyId)
      dispatch(contentModuleActions.invalidateList('study-agenda', nextStudyId))
    }


    if(match.params.courseSlug) {

      // non-agenda route.  useful to have course info
      dispatch(courseActions.fetchSingleBySlug(match.params.courseSlug)).then((courseAction) => {
        if(courseAction.success) {
          dispatch(sponsorActions.fetchSingleIfNeeded(courseAction.item._organization));
        }
      });
    } else {
      // want to make sure the old course info doesn't persist on agenda routes
      dispatch(courseActions.invalidateSelected())
    }

    dispatch(contentModuleActions.fetchSingleBySlug(match.params.contentModuleSlug)).then((tmAction) => {

      // 1.14 send user access tracking
      dispatch(userEventActions.sendCreateUserEvent({
        eventType: "user-access"
        , eventAction: "content-module-conclusion"
        , _contentModule: tmAction.item._id
      }));

      dispatch(userNodeActions.fetchListIfNeeded('_user', loggedInUser._id, '_contentModule', tmAction.item._id));
      dispatch(nodeActions.fetchListIfNeeded('_contentModule', tmAction.item._id, 'checkpoint', true)).then((checkpointNodeAction) => {
        let sceneIds = checkpointNodeAction.list && checkpointNodeAction.list.length > 0 ? checkpointNodeAction.list.map(node => node._scene) : [];
        dispatch(sceneActions.fetchListIfNeeded("_id", sceneIds));
      });
      dispatch(resourceLinkActions.fetchListIfNeeded('contentModule', tmAction.item._id));
      dispatch(evaluationActions.fetchListIfNeeded('_contentModule', tmAction.item._id, 'status', 'published')).then(tmAction => {
        // NOTE: we need this to only return one item.
        // console.log("ACTIONS", tmAction);

        // DEPREC 1.6.2
        // if(tmAction.list && tmAction.list.length > 0) {
        //   // 1.6.2 - this is no longer enough to fetch the evaluation. the user might have a valid one for a PREVIOUS userModule, but not for the current userModule
        //   dispatch(userEvaluationActions.fetchSingleUserEvaluationByEvaluation(tmAction.list[0]._id))
        // }
      })
      dispatch(moduleCertificateActions.fetchListIfNeeded('_user', loggedInUser._id, '_contentModule', tmAction.item._id)).then(certAction => {
        
        // const latestCert =  _.orderBy(certAction.list, [item => item.updated], ['desc'])[0];
        // const hasValidCert = ((latestCert && !latestCert.expires) || (latestCert && DateTime.fromISO(latestCert.expires) >= DateTime.local()));
        const latestCert = certUtils.getLatestCert(certAction.list);
        const hasValidCert = certUtils.checkIfValid(latestCert, tmAction.item);
        
        // on the initial visit by a user, the cert might not exist yet, so we need to call a second time down below
        if(hasValidCert) {
          // fetch redirect strategy and set to state
          dispatch(moduleCertificateActions.fetchModuleRedirectStrategies(latestCert._id)).then((strategyAction) => {
            console.log("STRATEGY ACTION 1", strategyAction);
            if(strategyAction.success && strategyAction.redirectStrategies && strategyAction.redirectStrategies.length > 0) {
              console.log("SETTING REDIRECT STRATEGIES 1", strategyAction.redirectStrategies);
              this.setState({ redirectStrategies: strategyAction.redirectStrategies }); // array of objects
            }
          });
        } else {
          this.setState({isLoading: true})
        }

        dispatch(userModuleActions.fetchSingleUserModuleByModule(tmAction.item._id)).then(umAction => {
          if(!umAction.success) {
            alert("Error finding current User Module");
          } else if(!umAction.item && !hasValidCert) {
            // this is a big one. the user should NOT be able to view this without a valid "UserModule" object in the database. this could happen if they paste the url for the "conclusion" page directly in here.
            // TODO: this should redirect silently when we go live.
            this.setState({ startModuleAlert: true });
            // alert("ATTN: You must first start the Module.");
            // this.props.history.push(`${match.url.replace('/conclusion', '')}`)
          } else {
            dispatch(sentimentAnswers.fetchListIfNeeded('_userModule', umAction.item._id, '_contentModule', tmAction.item._id, '_user', loggedInUser._id)).then(faAction => {
              if(faAction.success && faAction.list.length == 0 && tmAction.item.showSentimentModal) {
                this.setState({ sentimentModalVisible: true });
              }
            })
            // also go ahead and update the "reachedConclusion" field of that dude
            if(tmAction.item.testable) {
              // since the content module is testable, go ahead and check if certs exist
              dispatch(userModuleActions.sendUpdateUserModule({
                ...umAction.item
                , reachedConclusion: true
              }))
              // 1.6.2 - fetch list of UserEvaluations for this UserModule
              userEvaluationActions.fetchListIfNeeded('_userModule', umAction._id); // does this work? should be umAction.item._id or umAction.id? -g There is no way this works, it's not even dispatched. -w
              this.setState({isLoading: false})
            } else {


              // invalid the old list so that the UI updates better
              dispatch(moduleCertificateActions.invalidateList('_user', loggedInUser._id, '_contentModule', tmAction.item._id))

              // not testable, that means they completed it
              dispatch(userModuleActions.sendUpdateUserModule({
                ...umAction.item
                , reachedConclusion: true
                , status: 'completed'
                , issuedDate: new Date()
                , triggerCertCheck: true // NOTE: the update action cam also trigger the "certificate" check on the server
              })).then(() => {
                // NOTE:  NOW we want to fetch the certs (since any new ones should have just been created)
                dispatch(moduleCertificateActions.invalidateList('_user', loggedInUser._id, '_contentModule', tmAction.item._id))
                dispatch(moduleCertificateActions.fetchListIfNeeded('_user', loggedInUser._id, '_contentModule', tmAction.item._id)).then(certAction => {
                  const latestCert = certUtils.getLatestCert(certAction.list);
                  const hasValidCert = certUtils.checkIfValid(latestCert, tmAction.item);

                  // in case the certificate was just now created, check for redirect strategies
                  if(hasValidCert) {
                    // fetch redirect strategy and set to state
                    dispatch(moduleCertificateActions.fetchModuleRedirectStrategies(latestCert._id)).then((strategyAction) => {
                      console.log("STRATEGY ACTION 2", strategyAction);
                      if(strategyAction.success && strategyAction.redirectStrategies && strategyAction.redirectStrategies.length > 0) {
                        console.log("SETTING REDIRECT STRATEGIES 2", strategyAction.redirectStrategies);
                        this.setState({ redirectStrategies: strategyAction.redirectStrategies }); // array of objects
                      }
                    });
                  }
                  this.setState({isLoading: false})
                })
              })
            }

          }
        })
      })
    });
  }

  _goToStartPage() {
    const { history, match } = this.props;
    history.push(`${match.url.replace('/conclusion', '')}`)
    // history.push(`${match.url.replace('conclusion', '')}`)
  }

  _onNodeClick(nodeId) {
    this.props.dispatch(sceneActions.invalidateSelected());
  }

  _toggleTOC() {
    this.setState({
      tocVisible: !this.state.tocVisible
    });
  }

  _toggleSentimentModal() {
    this.setState({ sentimentModalVisible: !this.state.sentimentModalVisible });
  }

  _closeSentimentModal() {
    const { history } = this.props;
    this.setState({ sentimentModalVisible: false });
    // history.push('/my-agenda'); <--- this should be updated to use the agendaUrl if we reenable this
  }

  _startEvaluation() {
    /**
     * NOTES
     * not sure where to put them other than here, some high level evaluation stuff
     * 1) if you fail an evaluation, you have to start the whole training over from the beginning
     *    - we talked about this, and it seems like the most in line with Dave's expectations
     * 2) there should only be one published evaluation per content module
     *
     */
    const {
      dispatch
      , match
      , evaluationStore
      , contentModuleStore
      , userModuleStore
    } = this.props;
    // get current stuff
    const contentModule = contentModuleStore.selected.getItem();
    const userModule = userModuleStore.selected.getItem();
    const evaluation = evaluationStore.util.getList('_contentModule', contentModuleStore.selected.id, 'status', 'published') ? evaluationStore.util.getList('_contentModule', contentModuleStore.selected.id, 'status', 'published')[0] : null;
    // create new UserEvaluation
    dispatch(userEvaluationActions.sendCreateUserEvaluation({
      _evaluation: evaluation._id
      , _contentModule: contentModule._id
      , _userModule: userModule._id
    })).then(json => {
      if(json.success) {
        // created new one, now send them to it
        this.props.history.push(match.url.replace('conclusion', 'evaluations/' + evaluation._id))
      } else {
        alert(json.message);
      }
    })
  }

  _onVideoComplete() {
    console.log("1 VIDEO COMPLETED");
  }

  render() {
    // console.log("RENDER SINGLE CONTENT MODULE");
    const {
      courseStore
      , evaluationStore
      , location
      , loggedInUser
      , match
      , moduleCertificateStore
      , nodeStore
      , resourceLinkStore
      , sceneStore
      , sponsorStore
      , contentModuleStore
      , userEvaluationStore
      , userModuleStore
      , userNodeStore
    } = this.props;
    const agendaUrl = `${window.agendaUrl}/agenda`;
    /**
     * use the selected.getItem() utility to pull the actual contentModule object from the map
     */
    const selectedContentModule = contentModuleStore.selected.getItem();
    const selectedSponsor = sponsorStore.selected.getItem();
    const userModule = userModuleStore.selected.getItem();
    let conclusionSources = [];
    const evaluation = evaluationStore.util.getList('_contentModule', contentModuleStore.selected.id, 'status', 'published') ? evaluationStore.util.getList('_contentModule', contentModuleStore.selected.id, 'status', 'published')[0] : null;
    // const userEvaluation = userEvaluationStore.selected.getItem(); // might be empty or "failed"
    // find latest userEvaluation
    let allUserEvaluations = userEvaluationStore.util.getList('_userModule', userModule ? userModule._id : null);
    let userEvaluation;
    if(allUserEvaluations && allUserEvaluations.length > 0) {
      allUserEvaluations.sort((a, b) => b.updated - a.updated)
      userEvaluation = allUserEvaluations[0];
    }
    const selectedCourse = courseStore.selected.getItem();
    const isEmpty = (
      !selectedContentModule
      || !selectedContentModule._id
      || contentModuleStore.selected.didInvalidate
      // || !evaluation // not all have evaluation
      // || !selectedSponsor
      // || !userModule
    );

    let resourceLinks;
    let moduleCertificates;
    let moduleCertificateListInfo;
    let checkpointNodes = [];
    let previouslyViewedNodeIds = [];
    let cpScenes = null;
    if(!isEmpty) {
      conclusionSources = videoUtils.returnVideoSources(selectedContentModule.conclusionVidPath, selectedContentModule.conclusionPosterPath, false, selectedContentModule._id);
      resourceLinks = resourceLinkStore.util.getList('contentModule', selectedContentModule._id);
      moduleCertificateListInfo = moduleCertificateStore.lists
                                  && moduleCertificateStore.lists['_user']
                                  && moduleCertificateStore.lists['_user'][loggedInUser._id]
                                  && moduleCertificateStore.lists['_user'][loggedInUser._id]['_contentModule']
                                  && moduleCertificateStore.lists['_user'][loggedInUser._id]['_contentModule'][selectedContentModule._id];
      moduleCertificates = moduleCertificateStore.util.getList('_user', loggedInUser._id, '_contentModule', selectedContentModule._id);
      checkpointNodes = nodeStore.util.getList("_contentModule", selectedContentModule._id, "checkpoint", true);
      let cpNodeSceneIds = checkpointNodes && checkpointNodes.length > 0 ? checkpointNodes.map(node => node._scene) : [];
      cpScenes = sceneStore.util.getList("_id", cpNodeSceneIds) || null;
      // this should be narrowed down by the userModule, shouldn't it? -g
      previouslyViewedNodeIds = userNodeStore.util.getList('_user', loggedInUser._id, '_contentModule', selectedContentModule._id) || [];
      previouslyViewedNodeIds = [... new Set(previouslyViewedNodeIds.map(userNode => userNode._node))];
    }

    const isFetching = (
      contentModuleStore.selected.isFetching
      || !moduleCertificateListInfo
      // || moduleCertificateListInfo && moduleCertificateListInfo.isFetching
      // || sponsorStore.selected.isFetching
    )

    console.log("moduleCertificateListInfo", moduleCertificateListInfo)

    const noLinks = (
      !resourceLinks
      || resourceLinks.length < 1
    )

    const noCerts = (
      !moduleCertificates
      || moduleCertificates.length < 1
    )

    // NOTE: for testing only
    // let hasValidCert = true;
    let latestCert;
    let userModuleTiedToLatestCert = false;
    if(!noCerts) {
      latestCert = certUtils.getLatestCert(moduleCertificates);
      if(userModule) {
        userModuleTiedToLatestCert = latestCert._userModule == "" + userModule._id;
      }
    }
    // console.log("noCerts",noCerts,"latestCert", latestCert)
    const hasValidCert = certUtils.checkIfValid(latestCert, selectedContentModule);
    const hasRedirectStrategies = this.state.redirectStrategies.length > 0;

    const progressPercent = userModule ? userModule.progress : 0; //how? # nodes? # "checkpoints"? should be set and tracked on the UserModule, because if they complete the quiz at the end it should be 100%
    let progressClass = classNames(
      `progress-bar-${progressPercent}`
    )
    let tocClass = classNames(
      "-toc menu-toggle"
      , { '-open': this.state.tocVisible }
    )

    return (
      <div className="master-layout">
        <Helmet
          title={"Module Conclusion | Pro-ficiency"}
        />

        <div className="flex tm-overview">
          <section className="section">
            <div className="yt-container">
              <div style={{marginBottom: '20px'}} className="-buttons">
                <a href={`${window.agendaUrl}/agenda`} className="yt-btn -exit-btn">
                  <i className="fas fa-arrow-left" style={{marginRight: '10px'}}/>
                  Exit to Agenda
                </a>
              </div>
              {isEmpty ?
                (
                  isFetching ?
                    <div className="u-centerText">
                      <div className="loading"></div>
                    </div>
                    :
                    <h2>Empty.</h2>
                )
                :
                <div style={{ opacity: isFetching ? 0.5 : 1, width: "100%" }}>
                  {/**<div className="-progress-wrapper">
                      <div className={progressClass}>
                        <div className="-progress">
                          <div className="-complete">
                            { progressPercent === 100 ?
                              <img src="/img/progress-checkmark.png"/>
                              :
                              <img src="/img/progress-circle.png"/>
                            }
                          </div>
                        </div>
                      </div>
                    </div>*/}
                  <div className="cert-wrapper" style={{ marginBottom: '40px' }}>
                    {/* If they have a redirect we don't want to show the cert at all. Could not find where in the CSS the opacity was being applied to the overlay so took this simple route instead. -wes */}
                    <div style={hasRedirectStrategies ? { opacity: 0 } : {}}>
                    {hasValidCert ?
                      <AchievedModuleCertificate
                        moduleCertificate={latestCert}
                        sponsor={selectedSponsor}
                        contentModule={selectedContentModule}
                        user={loggedInUser}
                      />
                      :
                      <EmptyModuleCertificate
                        sponsor={selectedSponsor}
                        contentModule={selectedContentModule}
                      />
                    }
                    </div>
                    {latestCert && latestCert.status == "pending-attestation" && userModuleTiedToLatestCert ?
                      <div className="-overlay">
                        <div className="-content">
                          <h1>Pending</h1>
                          <p>Please finish the verification process to complete the training. Click here to view your pending Certificate.</p>
                          <div className="-buttons">
                            <a className="yt-btn large" href={`${window.agendaUrl}/certificates/${latestCert._id}`}>Certificate<i className="-icon far fa-arrow-right" /></a>
                          </div>
                        </div>
                      </div>
                      : (!moduleCertificateListInfo || moduleCertificateListInfo.isFetching || moduleCertificateListInfo.didInvalidate) || this.state.isLoading ?
                      <div className="-overlay">
                        <div className="-content">
                          {/* <h1>Loading....</h1> */}
                          <p>Loading...</p>
                          {/* <div className="-buttons">
                            <Link to={`${match.url.replace('/conclusion', '')}`} className="yt-btn large -start-btn"><i className="-icon far fa-arrow-left" /> Back to overview</Link>
                          </div> */}
                        </div>
                      </div>
                      : !hasValidCert && !selectedContentModule.testable ?
                        <div className="-overlay">
                          <div className="-content">
                            <h1>Incomplete</h1>
                            <p>Please complete the module to unlock your certificate</p>
                            <div className="-buttons">
                              <Link to={`${match.url.replace('/conclusion', '')}`} className="yt-btn large -start-btn"><i className="-icon far fa-arrow-left" /> Back to overview</Link>
                            </div>
                          </div>
                        </div>
                        : latestCert && latestCert.status == "expired" && selectedContentModule.testable && (userModuleTiedToLatestCert || !userModule) ?
                          <div className="-overlay">
                            <div className="-content">
                              <h1>Your Certificate Has Expired</h1>
                              <p>You must re-take the training.</p>
                              <div className="-buttons">
                                <a className="yt-btn large" href={agendaUrl}>My Agenda <i className="-icon far fa-arrow-right" /></a>
                              </div>
                            </div>
                          </div>
                          : latestCert && latestCert.status === 'expiring-soon' && selectedContentModule.testable && (userModuleTiedToLatestCert || !userModule) ?
                            <div className="-overlay">
                              <div className="-content">
                                <h1>Your certificate is expiring soon</h1>
                                <p>You must re-take the training.</p>
                                <div className="-buttons">
                                  <a className="yt-btn large" href={agendaUrl}>My Agenda <i className="-icon far fa-arrow-right" /></a>
                                </div>
                              </div>
                            </div>
                            : latestCert && latestCert.status === 'expiring-soon' && userModule && selectedContentModule.testable ?
                              <div className="-overlay -failed">
                                <div className="-content">
                                  <h1>Your certificate is expiring soon</h1>
                                  <p>You must retake & pass the exam to unlock your certificate.</p>
                                  <div className="-buttons">
                                    <button className="yt-btn large danger -retake-btn" onClick={this._startEvaluation}>Retake exam</button>
                                  </div>
                                </div>
                              </div>
                              : !hasValidCert && selectedContentModule.testable && userModule && !userEvaluation ?
                                <div className="-overlay">
                                  <div className="-content">
                                    <h1>Unlock certificate</h1>
                                    <p>You must pass the exam to receive your certificate of completion.</p>
                                    <div className="-buttons">
                                      <button className="yt-btn large -start-btn" onClick={this._startEvaluation}>Start exam <i className="-icon far fa-arrow-right" /></button>
                                    </div>
                                    <p>NOTE: If you leave the exam before finishing you may have to start over from the beginning.</p>
                                  </div>
                                </div>
                                : !hasValidCert && selectedContentModule.testable && userEvaluation && userEvaluation.status === 'failed' ?
                                  <div className="-overlay -failed">
                                    <div className="-content">
                                      <h1>Exam failed!</h1>
                                      <p>You must pass the exam to unlock your certificate.</p>
                                      <div className="-buttons">
                                        <button className="yt-btn large danger -retake-btn" onClick={this._startEvaluation}>Retake exam</button>
                                      </div>
                                    </div>
                                  </div>
                                  : !hasValidCert && selectedContentModule.testable && userEvaluation && userEvaluation.status === 'started' && evaluation && evaluation._id ?
                                    <div className="-overlay">
                                      <div className="-content">
                                        <h1>Continue exam</h1>
                                        <p>You must pass the exam to unlock your certificate.</p>
                                        <div className="-buttons">
                                          <Link className="yt-btn large" to={`${match.url.replace('conclusion', 'evaluations/' + evaluation._id + (userEvaluation._lastQuestion ? '/q/' + userEvaluation._lastQuestion : ''))}`}>Continue <i className="-icon far fa-arrow-right" /></Link>
                                        </div>
                                      </div>
                                    </div>
                                    :
                                    <div className="-overlay">
                                      <div className="-redirect-content">
                                        {hasRedirectStrategies ?
                                          <div className="redirect-strategies">
                                            {this.state.redirectStrategies.map((strategy, i) => {
                                              return (
                                                <div className='yt-col' style={{ backgroundColor: 'white', color: 'black', padding: '2em', borderRadius: '8px', marginBottom: '1em', textAlign: 'left', boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.4)' }}>
                                                  <h2 style={{ marginBottom: '1em'}}>Congratulations! 🎉</h2>
                                                  <p style={{ color: 'black', textAlign: 'left', marginBottom: '1em' }}>You've completed the Pro-ficiency program!</p>
                                                  <p style={{ color: 'black', textAlign: 'left', marginBottom: '2em' }}>Next steps required for {strategy.groupName}</p>
                                                  <div key={i} style={{ marginBottom: '2em'}}>
                                                    <a
                                                      className='yt-btn block'
                                                      style={{ backgroundColor: '#C14D22', color: 'white', borderRadius: '30px', fontSize: '18px' }}
                                                      href={strategy.url}
                                                      target="_blank"
                                                    >
                                                      {strategy.label ? strategy.label : 'Continue'}
                                                    </a>
                                                  </div>
                                                  <p style={{ color: 'black', textAlign: 'center', fontSize: '15px', color: '#626F7F'}}>Questions? Please contact
                                                    <a href={`mailto:prosupport@simulations-plus.com?`} style={{color: '#276AB5', textDecoration: 'underline', marginLeft: '5px'}}>prosupport@simulations-plus.com</a>
                                                  </p>
                                                </div>
                                              )
                                            })}
                                          </div>
                                          :
                                          <div>
                                            <h1>Congratulations!</h1>
                                            <div className="-buttons">
                                              <a href={`${window.agendaUrl}/certificates${latestCert ? '/' + latestCert._id : ''}`} target="_blank" className="yt-btn large -print-btn"><i className="-icon fal fa-print" /> View & print certificate</a>
                                            </div>
                                            <br />
                                            <p ><strong>You may have additional modules to complete. Click continue below to go back to Agenda and check your progress.</strong></p>
                                            <div className="-buttons yt-row space-around">
                                              {/* <Scrollchor to="#conclusion" className="yt-btn -trailer-btn">Review Content <i className="-icon fas fa-arrow-circle-down" /></Scrollchor> */}
                                              <a href={`${window.agendaUrl}/agenda`} className="yt-btn -start-btn" >Continue <i className="-icon fas fa-arrow-right" /></a>
                                            </div>
                                          </div>
                                        }

                                      </div>
                                    </div>
                    }
                  </div>
                  {/* BELOW REMOVED for ATHENS. This is setup to link to REDSTONE training, and is not necessary on this conclusion page, so rather than updating the links somehow we're just getting rid. */}
                  {/* <div className="scene-controls">
                    <div className={tocClass} onClick={this._toggleTOC}>
                      <div className="-icons">
                        <i className="-bars fal fa-bars fa-2x" />
                        <i className="-close fal fa-times fa-2x" />
                      </div>
                    </div>
                    <div className="yt-col">
                      <div className="checkpoint-markers">
                        {cpScenes && cpScenes.length > 0 ?
                          checkpointNodes.map((node, i) =>
                            <CheckpointMarker
                              key={node._id + i}
                              node={node}
                              onNodeClick={this._onNodeClick}
                              reachedConclusion={userModule ? userModule.reachedConclusion : false}
                              scene={sceneStore.byId[node._scene]}
                              viewedNodes={previouslyViewedNodeIds}
                            />
                          )
                          :
                          null
                        }
                        {!hasValidCert && selectedContentModule.testable && evaluation && evaluation._id && userEvaluation && userEvaluation.status === 'started' ?
                          <Link
                            to={`${match.url.replace('conclusion', 'evaluations/' + evaluation._id + (userEvaluation._lastQuestion ? '/q/' + userEvaluation._lastQuestion : ''))}`}
                            className="-marker -viewed"
                          >
                            <ReactTooltip
                              aria-haspopup='true'
                              className="yt-tooltip"
                              id={`tm-evaluation`}
                              place="top"
                              type="dark"
                            >
                              <p>Continue exam</p>
                            </ReactTooltip>
                            <div className="-icon" >
                              <i className="far fa-clipboard-list fa-lg" />
                            </div>
                            <div className="-super" data-tip data-for={`tm-evaluation`} >
                              <i className="fal fa-clipboard-list fa-lg" />
                            </div>
                          </Link>
                          : !hasValidCert && selectedContentModule.testable ?
                            <span
                              className="-marker -clickable"
                              onClick={this._startEvaluation}
                            >
                              <ReactTooltip
                                aria-haspopup='true'
                                className="yt-tooltip"
                                id={`tm-evaluation`}
                                place="top"
                                type="dark"
                              >
                                <p>Start exam</p>
                              </ReactTooltip>
                              <div className="-icon" >
                                <i className="far fa-clipboard-list fa-lg" />
                              </div>
                              <div className="-super" data-tip data-for={`tm-evaluation`} >
                                <i className="fal fa-clipboard-list fa-lg" />
                              </div>
                            </span>
                            : hasValidCert && selectedContentModule.testable ?
                              <span
                                className="-marker -viewed"
                                onClick={this._startEvaluation}
                              >
                                <ReactTooltip
                                  aria-haspopup='true'
                                  className="yt-tooltip"
                                  id={`tm-evaluation`}
                                  place="top"
                                  type="dark"
                                >
                                  <p>Exam passed!</p>
                                </ReactTooltip>
                                <div className="-icon" >
                                  <i className="far fa-clipboard-check fa-lg" />
                                </div>
                                <div className="-super" data-tip data-for={`tm-evaluation`} >
                                  <i className="fas fa-clipboard-check fa-lg" />
                                </div>
                              </span>
                              :
                              null
                        }
                        {!hasValidCert ?
                          <span
                            className="-marker -viewed"
                          >
                            <ReactTooltip
                              aria-haspopup='true'
                              className="yt-tooltip"
                              id={`tm-conclusion`}
                              place="top"
                              type="dark"
                            > {!selectedContentModule.testable ?
                              <p><strong>Incomplete! </strong> Please complete the module to unlock your certificate</p>
                              :
                              <p><strong>Note:</strong> You must pass the exam to receive your certificate</p>
                              }
                            </ReactTooltip>
                            <div className="-icon" >
                              <i className="fas fa-exclamation-triangle fa-lg" />
                            </div>
                            <div className="-super" data-tip data-for={`tm-conclusion`} >
                              <i className="fal fa-exclamation-triangle fa-lg" />
                            </div>
                          </span>
                          :
                          null
                        }
                        {hasValidCert ?
                          <span
                            className="-marker -viewed"
                          >
                            <ReactTooltip
                              aria-haspopup='true'
                              className="yt-tooltip"
                              id={`tm-conclusion`}
                              place="top"
                              type="dark"
                            >
                              <p>Completed module!</p>
                            </ReactTooltip>
                            <div className="-icon" >
                              <i className="far fa-badge-check fa-lg" />
                            </div>
                            <div className="-super" data-tip data-for={`tm-conclusion`} >
                              <i className="fas fa-badge-check fa-lg" />
                            </div>
                          </span>
                          :
                          null

                        }

                      </div>
                    </div>
                  </div> */}
                  {/* <div className="yt-row stretch">
                    <ContentModuleTableOfContents
                      isConclusionPage={true}
                      visible={this.state.tocVisible}
                      previouslyViewedNodeIds={previouslyViewedNodeIds}
                      selectedContentModule={selectedContentModule}
                    />
                    <div className="yt-col">


                      <div id="conclusion" className="content-container">

                        <div className="yt-row with-gutters">
                          <div className="yt-col full m_60 ">
                            <h1> {selectedContentModule.title} </h1>
                          </div>
                          <div className="yt-col full m_40 ">
                            {selectedContentModule.conclusionVidPath ?
                              <div>
                                <VideoPlayer
                                  isAutoPlay={false}
                                  onVideoComplete={this._onVideoComplete}
                                  playerId={selectedContentModule._id}
                                  playlist={videoUtils.returnPlaylist(selectedContentModule.conclusionVidPath, selectedContentModule.conclusionPosterPath, null)}
                                  tracking={{
                                    _course: courseStore.selected.id ? courseStore.selected.id : null
                                    , _contentModule: selectedContentModule._id
                                    , description: "conclusion-page"
                                  }}
                                />
                                <br />
                              </div>
                              :
                              null
                            }
                            {noLinks ?
                              null
                              :
                              <div>
                                <h3><strong>Resources</strong></h3>
                                <div className="resource-links">
                                  {resourceLinks.map((rLink, i) =>
                                    <ResourceLinkListItem
                                      dispatch={this.props.dispatch}
                                      key={i}
                                      resourceLink={rLink}
                                    />
                                  )}
                                </div>
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div> */}
                  <AlertModal
                    alertMessage="You must first start the module before you can take the exam."
                    alertTitle={"ERROR: module not started"}
                    closeAction={this._goToStartPage}
                    confirmAction={this._goToStartPage}
                    confirmText={"Okay"}
                    isOpen={this.state.startModuleAlert}
                    type="danger"
                  />
                  <UserSentimentModal
                    closeModal={() => this._closeSentimentModal()}
                    dispatch={this.props.dispatch}
                    isOpen={this.state.sentimentModalVisible}
                    contentModule={selectedContentModule}
                    userModule={userModule}
                    loggedInUser={loggedInUser}
                  />
                </div>
              }
            </div>
          </section>
        </div>
      </div>
    )
  }
}

ContentModuleConclusion.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store, ownProps) => {
  /**
   * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
   * differentiated from the React component's internal state
   */

  return {
    courseStore: store.course
    , evaluationStore: store.evaluation
    , loggedInUser: store.user.loggedIn.user
    , moduleCertificateStore: store.moduleCertificate
    , nodeStore: store.node
    , resourceLinkStore: store.resourceLink
    , sceneStore: store.scene
    , sponsorStore: store.sponsor
    , studyStore: store.study
    , contentModuleStore: store.contentModule
    , userEvaluationStore: store.userEvaluation
    , userModuleStore: store.userModule
    , userNodeStore: store.userNode
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(ContentModuleConclusion)
);




// WEBPACK FOOTER //
// modules/contentModule/views/ContentModuleConclusion.js.jsx